/**
 * Battle action log: server-authoritative display.
 * Shows up to 5 entries with order images; arrow buttons to navigate.
 * Tooltip on hover with details (visualization + minimal text).
 */
import React, { useState, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import BattleActionLogTooltip from './BattleActionLogTooltip';
import './BattleActionLog.css';

const DISPLAY_LIMIT = 5;

function getOrderImageSrc(entry) {
  const primary = entry.unit || entry.order || entry.attacker || entry.drone || entry.weapon || entry.strategem || entry.source;
  if (primary?.id) return `/images/${primary.id}.jpg`;
  return '/images/default.jpg';
}

function getPrimaryName(entry) {
  const primary = entry.unit || entry.order || entry.attacker || entry.drone || entry.weapon || entry.strategem || entry.source;
  return primary?.name || 'Unknown';
}

/** Border theme for strip thumbnail: 'aggressive' (red), 'positive' (green), or default (gray). */
function getItemBorderTheme(entry) {
  if (!entry?.type) return null;
  const aggressive = [
    'unit-attacked',
    'unit-attacked-general',
    'unit-killed',
    'strategem-damaged-unit',
    'strategem-damaged-general',
    'strategem-destroyed-unit',
    'unit-special-effect-damage',
    'drone-damage'
  ];
  const positive = [
    'strategem-grew-unit',
    'strategem-spawned-unit'
  ];
  if (aggressive.includes(entry.type)) return 'aggressive';
  if (positive.includes(entry.type)) return 'positive';
  return null; // gray (deployments + unclassified)
}

function BattleActionLog({ actionLog = [], gameState, userId, opponentId }) {
  const { t } = useTranslation();
  const [viewOffset, setViewOffset] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  const total = Array.isArray(actionLog) ? actionLog.length : 0;
  const maxOffset = Math.max(0, total - DISPLAY_LIMIT);
  const clampedOffset = Math.min(viewOffset, maxOffset);

  // Keep the view pinned to the newest entries: when the log grows or on load, show the latest 5.
  useLayoutEffect(() => {
    if (total > DISPLAY_LIMIT) {
      setViewOffset(Math.max(0, total - DISPLAY_LIMIT));
    }
  }, [total]);

  // Strip order: oldest (index 0) left, newest (last index) right. Slice is already [oldest..newest].
  const displayed = Array.isArray(actionLog)
    ? actionLog.slice(clampedOffset, clampedOffset + DISPLAY_LIMIT)
    : [];
  const canGoLeft = clampedOffset > 0;
  const canGoRight = total > DISPLAY_LIMIT && clampedOffset < maxOffset;

  const showTooltip = useCallback((element, index) => {
    const rect = element.getBoundingClientRect();
    setTooltipPosition({
      top: rect.bottom + window.scrollY + 8,
      left: rect.left + window.scrollX + rect.width / 2
    });
    setHoveredIndex(index);
  }, []);

  if (total === 0) {
    return (
      <div className="battle-action-log-container" aria-label={t('battleUI.actionHistory.title')}>
        <span className="battle-action-log-empty">{t('battleUI.actionHistory.noEvents')}</span>
      </div>
    );
  }

  return (
    <div className="battle-action-log-container" role="region" aria-label={t('battleUI.actionHistory.title')}>
      {canGoLeft && (
        <button
          type="button"
          className="battle-action-log-nav battle-action-log-nav-left"
          onClick={(e) => { e.stopPropagation(); setViewOffset(o => Math.max(0, o - DISPLAY_LIMIT)); }}
          aria-label={t('battleUI.actionHistory.older')}
        >
          ‹
        </button>
      )}
      <div className="battle-action-log-strip">
        {displayed.map((entry, index) => {
          const globalIndex = clampedOffset + index;
          const src = getOrderImageSrc(entry);
          const name = getPrimaryName(entry);
          const borderTheme = getItemBorderTheme(entry);
          const itemClass = ['battle-action-log-item', borderTheme ? `battle-action-log-item--${borderTheme}` : ''].filter(Boolean).join(' ');
          return (
            <div
              key={entry.sequence ?? globalIndex}
              className={itemClass}
              onMouseEnter={(e) => showTooltip(e.currentTarget, globalIndex)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <img
                src={src}
                alt={name}
                onError={(e) => { e.target.onerror = null; e.target.src = '/images/default.jpg'; }}
              />
            </div>
          );
        })}
      </div>
      {canGoRight && (
        <button
          type="button"
          className="battle-action-log-nav battle-action-log-nav-right"
          onClick={(e) => { e.stopPropagation(); setViewOffset(o => Math.min(maxOffset, o + DISPLAY_LIMIT)); }}
          aria-label={t('battleUI.actionHistory.newer')}
        >
          ›
        </button>
      )}
      {hoveredIndex !== null && actionLog[hoveredIndex] && createPortal(
        <BattleActionLogTooltip
          entry={actionLog[hoveredIndex]}
          gameState={gameState}
          userId={userId}
          opponentId={opponentId}
          position={tooltipPosition}
        />,
        document.body
      )}
    </div>
  );
}

export default BattleActionLog;
